import { isIosCompanionApp } from "@/lib/native/runtime";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useStoreSubscriptionQuery } from "@/shared/subscriptions/services/useStoreSubscriptionQuery";

type Props = {
  storeSlug: string;
};

/**
 * Global banner shown across the seller portal when the store's subscription is
 * restricted (free trial ended / payment overdue, and not premium). It tells
 * the seller their account is read-only until payment. Renders nothing in the
 * normal (active/premium) case.
 */
export function SubscriptionRestrictedBanner({ storeSlug }: Props) {
  const t = useTranslations();
  const { data } = useStoreSubscriptionQuery({ storeSlug });

  if (!data?.isRestricted) {
    return null;
  }

  return (
    <div
      role="alert"
      className="border-b border-[color:var(--error-200,#f5c2c2)] bg-[color:var(--error-100)] px-4 py-3 text-[color:var(--error-700,#8a1f1f)]"
    >
      <p className="text-sm font-semibold">
        {t(keys.seller.subscriptionRestrictedTitle)}
      </p>
      <p className="mt-1 text-xs">
        {/*
          A partly paid period is the predicted support ticket: the seller
          paid what they were asked and is still locked out, because held
          orders were settled first. Say so, and say where the breakdown is.
        */}
        {isIosCompanionApp() ? t(keys.iosCompanion.restricted) : data.isPartiallyPaid
          ? t(keys.seller.subscriptionRestrictedPartialMessage)
          : t(keys.seller.subscriptionRestrictedMessage)}
      </p>
    </div>
  );
}
